import { Box } from "@mui/material";
import type React from "react";
import { useContext, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

// @ts-ignore
import type { SearchMessageResult } from "@storyboard/admin-client";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import PodcastItem from "@/components/Search/PodcastItem";
import { ampli } from "@/config";
import { channelPath } from "@/hooks/dispatch/channelPath";
import { useDispatchEnabled } from "@/hooks/dispatch/useDispatchEnabled";
import { UxContext } from "@/models/UxStateProvider";
import { useCollectionRow } from "@/store/useCollectionsCompute";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";
import SearchItemHeader from "./SearchItemHeader";

export default function MessageItem({
  active,
  activeLinkIndex,
  id,
  itemIndex,
  lastItem,
  message,
}: {
  active: boolean;
  activeLinkIndex: number;
  id: string;
  itemIndex: number;
  lastItem: boolean;
  message: SearchMessageResult;
}) {
  const { setGlobalSearchOpen } = useContext(UxContext);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const feedRecord = useCollectionRow("feed", message.feedId);
  const dispatchEnabled = useDispatchEnabled(message.workspaceId);
  const feedPath = channelPath({
    workspaceId: message.workspaceId,
    feedId: message.feedId,
    aliasMembershipId: feedRecord?.aliasMembershipId,
    dispatchEnabled,
  });
  // The Dispatch History pane opens on the newest messages; only the older views scroll to the one found.
  const opensInDispatch = dispatchEnabled && Boolean(feedRecord?.aliasMembershipId);
  const url = opensInDispatch ? feedPath : `${feedPath}?feedItemId=${message.feedItemId}`;
  const isPodcast = message?.displayTitle?.length > 0 || message?.displayDescription?.length > 0;
  const isPodcastLink = isPodcast && "pod-cast";
  const activeLink = active && "active";
  const lastItemLink = lastItem && "last-item";
  const linkClassName = `search-item ${isPodcastLink} ${activeLink} ${lastItemLink}`;
  const navigate = useNavigate();

  const clickEvent = async (e?: React.MouseEvent<HTMLAnchorElement>) => {
    if (e) {
      e.preventDefault();
    }
    setGlobalSearchOpen(() => false);
    ampli.searchLinkToMessage({ workspaceId: message.workspaceId });
    navigate(url);
  };

  /**
   * Keyboard enter event handler
   */
  useEffect(() => {
    if (active && activeLinkIndex === itemIndex && linkRef.current) {
      clickEvent().then(() => {
        navigate(url);
      });
    }
  }, [active, activeLinkIndex, active, itemIndex, clickEvent]);

  return (
    <Link
      ref={linkRef}
      id={id}
      data-sb={TestIds.search.message}
      to={url}
      className={linkClassName}
      onClick={(e) => clickEvent(e)}
    >
      <Box>
        <SearchItemHeader message={message} />
        {isPodcast && <PodcastItem message={message} />}
        <Box
          dangerouslySetInnerHTML={{
            __html: sanitizeHtmlForDisplay(message?.fullTranscription ?? ""),
          }}
        />
      </Box>
    </Link>
  );
}
